import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { HiOutlineInformationCircle } from 'react-icons/hi';

const REGULATOR_APPLICABILITY_ACK_KEY = 'regulator_applicability_ack_v1';

function readAck() {
  try {
    return localStorage.getItem(REGULATOR_APPLICABILITY_ACK_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeAck() {
  try {
    localStorage.setItem(REGULATOR_APPLICABILITY_ACK_KEY, '1');
  } catch (_) {
    // Best-effort; if storage is blocked, the modal may reappear.
  }
}

export default function RegulatorGate() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => !readAck());

  const acknowledgeAndClose = useCallback(() => {
    writeAck();
    setOpen(false);
  }, []);

  const openTechnicalDetails = useCallback(() => {
    writeAck();
    setOpen(false);
    navigate('/regulator/technical-details');
  }, [navigate]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') acknowledgeAndClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [acknowledgeAndClose, open]);

  const copy = useMemo(
    () => ({
      title: 'PolicyPlan — Notice',
      intro: [
        'The PolicyPlan module helps policymakers evaluate truck activity, charging infrastructure, grid readiness, community characteristics, and related planning factors across locations within a region.',
      ],
      applicabilityTitle: 'Data applicability',
      applicabilityBody: [
        'Most datasets in this module are primarily built for Southern California.',
        'When used outside this region, coverage may be incomplete. Please use with caution.',
      ],
      primaryCta: 'Understand and Continue',
      secondaryCta: 'View Technical Details',
    }),
    []
  );

  return (
    <>
      <Outlet />
      {open && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Regulator applicability notice"
          onMouseDown={(e) => {
            // Click outside closes (and acknowledges) to avoid trapping users.
            if (e.target === e.currentTarget) acknowledgeAndClose();
          }}
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex items-start gap-3 p-6 border-b border-gray-100">
              <div className="mt-0.5 p-2 rounded-xl bg-blue-50">
                <HiOutlineInformationCircle className="text-blue-600" size={22} />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 font-ui">{copy.title}</h2>
                <div className="mt-3 space-y-2 text-sm text-gray-700 leading-relaxed">
                  {copy.intro.map((t) => (
                    <p key={t}>{t}</p>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={acknowledgeAndClose}
                className="ml-auto -mr-1 rounded-md px-2 py-1 text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{copy.applicabilityTitle}</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 leading-relaxed">
                {copy.applicabilityBody.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 p-6 pt-0">
              <button
                type="button"
                onClick={openTechnicalDetails}
                className="sm:order-1 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {copy.secondaryCta}
              </button>
              <button
                type="button"
                onClick={acknowledgeAndClose}
                className="sm:order-2 inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                autoFocus
              >
                {copy.primaryCta}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

